import React, { useCallback, useEffect, useState } from 'react';
import { ErrorAlert } from '../../../shared/components/ErrorAlert';
import { Spinner } from '../../../shared/components/Spinner';
import { useAuth } from '../../../contexts/AuthContext';
import { fetchAdminCompanyUsers } from '../../../services/admin/adminCompanyService';
import { UserImpersonationControls } from './UserImpersonationControls';
import type { AdminCompanyUser } from '../../../models/adminCompany';

export interface CompanyUsersSectionProps {
  companyId: string;
}

/** The 3PL company's own login accounts (operator/admin users) — distinct from its merchants. */
export function CompanyUsersSection({ companyId }: CompanyUsersSectionProps): React.ReactElement {
  const { getAccessToken } = useAuth();
  const [users, setUsers] = useState<AdminCompanyUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const list = await fetchAdminCompanyUsers(companyId, getAccessToken);
      setUsers(list);
    } catch (e) {
      setUsers([]);
      setError(e instanceof Error ? e.message : 'Failed to load company users.');
    } finally {
      setLoading(false);
    }
  }, [companyId, getAccessToken]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="bo-card">
      <div className="bo-admin-table-head">
        <h2 className="bo-admin-section-title">Company users</h2>
        <p className="bo-muted">This company&apos;s own login accounts (not its merchants).</p>
      </div>
      {loading ? <Spinner label="Loading users…" /> : null}
      {!loading && error ? <ErrorAlert message={error} /> : null}
      {!loading && !error && users.length === 0 ? (
        <p className="bo-muted">No login accounts found for this company.</p>
      ) : null}
      {!loading && !error && users.length > 0 ? (
        <div className="bo-admin-table-wrap">
          <table className="bo-admin-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Roles</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {users.map((user) => {
                const name = `${user.firstName} ${user.lastName}`.trim() || user.email;
                return (
                  <tr key={user.id}>
                    <td>{name}</td>
                    <td>{user.email || '—'}</td>
                    <td>{user.roles.length > 0 ? user.roles.join(', ') : '—'}</td>
                    <td>
                      <span
                        className={`bo-status-pill ${user.banned ? 'bo-status-inactive' : 'bo-status-active'}`}
                      >
                        {user.banned ? 'Suspended' : 'Active'}
                      </span>
                    </td>
                    <td>
                      <div className="bo-actions">
                        <UserImpersonationControls
                          targetUserId={user.id}
                          displayName={name}
                          disabled={user.banned}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
